import { useState, useEffect } from 'react';
import { API_BASE } from '../config';
import { formatDate } from '../utils/formatDate';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';
import imgScanBox from '../assets/kam-banner/scan-box.png';
import imgConveyor from '../assets/kam-banner/conveyor.png';
import imgCounter from '../assets/kam-banner/counter-scan.png';
import imgHandheld from '../assets/kam-banner/handheld.png';

const KAMS = ['Hari','Adarsh','Ishani','Ritik','Nikita','Tarun','Kunal','Kashish'];

export default function SoKamReview({ user }) {
    const isAdmin = user?.role === 'admin';

    const [view,          setView]          = useState('list'); // 'list' | 'detail' | 'vendors' | 'party_names'
    const [pendingSOs,    setPendingSOs]     = useState([]);
    const [selectedSO,    setSelectedSO]    = useState(null);
    const [lines,         setLines]         = useState([]);
    const [lineQtys,      setLineQtys]      = useState({});
    const [loading,       setLoading]       = useState(true);
    const [saving,        setSaving]        = useState(false);
    const [msg,           setMsg]           = useState(null);
    const [kamFilter,     setKamFilter]     = useState('');
    const [kamList,       setKamList]       = useState([]);
    const [vendors,       setVendors]       = useState([]);
    const [newVendor,     setNewVendor]     = useState({ vendor_name:'', kam_name:'' });
    const [vendorMsg,     setVendorMsg]     = useState(null);

    const [partyNames,    setPartyNames]    = useState([]);
    const [newPartyName,  setNewPartyName]  = useState({ raw_name:'', standard_name:'' });
    const [partyNameMsg,  setPartyNameMsg]  = useState(null);
    const [unmappedNames, setUnmappedNames] = useState([]);
    const [unmappedAssign,setUnmappedAssign]= useState({}); // raw_name -> chosen/typed standard name
    const [unmappedSaving,setUnmappedSaving]= useState({}); // raw_name -> bool

    const [bulkMode,        setBulkMode]        = useState(false);
    const [selectedVouchers,setSelectedVouchers]= useState([]);
    const [bulkLinesMap,    setBulkLinesMap]    = useState({});
    const [bulkSaving,      setBulkSaving]      = useState(false);

    const [autoApprove,     setAutoApprove]     = useState({ enabled:false, kam_name:null, loading:true });

    const [confirmModal,  setConfirmModal]  = useState(null); // { title, message, confirmLabel, onConfirm }

    const identityQS = `&username=${encodeURIComponent(user?.username||'')}&role=${encodeURIComponent(user?.role||'')}`;

    useEffect(() => {
        loadPending();
        if (isAdmin) loadKamList();
        const q = new URLSearchParams(window.location.search).get('q');
        if (q) openSO({ voucher_no: q });
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        loadAutoApproveStatus();
        // eslint-disable-next-line
    }, [kamFilter]);

    function adminHeaders() {
        return {
            'x-admin-user': user?.username || '',
            'x-admin-pass': localStorage.getItem('sf_pass') || '',
        };
    }

    async function loadPending(kam = kamFilter) {
        setLoading(true);
        try {
            const res  = await fetch(`${API_BASE}/so-kam-review?action=pending&kam=${encodeURIComponent(kam)}${identityQS}`);
            const data = await res.json();
            setPendingSOs(Array.isArray(data) ? data : []);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    }

    async function loadKamList() {
        try {
            const res  = await fetch(`${API_BASE}/so-kam-review?action=kam_list`, { headers: adminHeaders() });
            const data = await res.json();
            setKamList(Array.isArray(data) ? data : []);
        } catch(e) { console.error(e); }
    }

    async function loadVendors() {
        try {
            const res  = await fetch(`${API_BASE}/so-kam-review?action=kam_vendors`, { headers: adminHeaders() });
            const data = await res.json();
            setVendors(Array.isArray(data) ? data : []);
        } catch(e) { console.error(e); }
    }

    async function loadPartyNames() {
        try {
            const res  = await fetch(`${API_BASE}/so-kam-review?action=party_names`, { headers: adminHeaders() });
            const data = await res.json();
            setPartyNames(Array.isArray(data) ? data : []);
        } catch(e) { console.error(e); }
    }

    async function loadUnmappedNames() {
        try {
            const res  = await fetch(`${API_BASE}/so-kam-review?action=unmapped_party_names`, { headers: adminHeaders() });
            const data = await res.json();
            setUnmappedNames(Array.isArray(data) ? data : []);
        } catch(e) { console.error(e); }
    }

    async function loadAutoApproveStatus() {
        // For admin, auto-approve only makes sense when scoped to one KAM.
        if (isAdmin && !kamFilter) { setAutoApprove({ enabled:false, kam_name:null, loading:false }); return; }
        setAutoApprove(prev => ({ ...prev, loading:true }));
        try {
            const kamParam = isAdmin ? `&kam=${encodeURIComponent(kamFilter)}` : '';
            const res  = await fetch(`${API_BASE}/so-kam-review?action=auto_approve_status${kamParam}${identityQS}`);
            const data = await res.json();
            setAutoApprove({ enabled: !!data.enabled, kam_name: data.kam_name || null, loading:false });
        } catch(e) { console.error(e); setAutoApprove({ enabled:false, kam_name:null, loading:false }); }
    }

    async function toggleAutoApprove() {
        const turningOn = !autoApprove.enabled;
        const kamName   = isAdmin ? kamFilter : autoApprove.kam_name;
        if (!kamName) return;

        const doToggle = async () => {
            try {
                const res = await fetch(`${API_BASE}/so-kam-review?action=set_auto_approve`, {
                    method: 'PUT',
                    headers: { 'Content-Type':'application/json' },
                    body: JSON.stringify({ kam_name: kamName, enabled: turningOn, username: user?.username, role: user?.role })
                });
                const data = await res.json();
                if (res.ok && data.success) loadAutoApproveStatus();
            } catch(e) { console.error(e); }
        };

        if (turningOn) {
            setConfirmModal({
                title: 'Enable Auto Approve?',
                message: `Every new Sales Order for ${kamName} will be approved automatically from now on, without appearing in the pending review queue. You can turn this off again at any time.`,
                confirmLabel: 'Enable Auto Approve',
                onConfirm: doToggle,
            });
        } else {
            doToggle();
        }
    }

    async function openSO(so) {
        setSelectedSO(null); setLines([]); setLineQtys({});
        setView('detail');
        try {
            const res  = await fetch(`${API_BASE}/so-kam-review?action=detail&voucher_no=${encodeURIComponent(so.voucher_no)}${identityQS}`);
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                setMsg({ type:'error', text: res.status === 403 ? `SO ${so.voucher_no} belongs to another KAM's party.` : (text || `Could not open SO ${so.voucher_no}`) });
                setView('list');
                return;
            }
            const data = await res.json();
            setSelectedSO(data.header);
            setLines(data.lines || []);
            setLineQtys(Object.fromEntries((data.lines || []).map(l => [l.id, l.kam_validated_qty ?? l.quantity])));
        } catch(e) { console.error(e); }
    }

    async function doValidateAll() {
        if (!selectedSO) return;
        setSaving(true); setMsg(null);
        try {
            const res  = await fetch(`${API_BASE}/so-kam-review?action=validate_all`, {
                method: 'PUT',
                headers: { 'Content-Type':'application/json' },
                body: JSON.stringify({
                    voucher_no:   selectedSO.voucher_no,
                    validated_by: user?.display_name || user?.username || 'KAM',
                    role: user?.role, username: user?.username,
                    lines: lines.map(l => ({ line_id: l.id, kam_validated_qty: parseFloat(lineQtys[l.id]) })),
                })
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setMsg({ type:'success', text:`SO ${selectedSO.voucher_no} approved successfully` });
                setSelectedSO(prev => ({ ...prev, kam_validated: 1, kam_validated_by: user?.display_name || user?.username }));
                setLines(prev => prev.map(l => ({ ...l, kam_validated_qty: parseFloat(lineQtys[l.id]), kam_variance: parseFloat(lineQtys[l.id]) - parseFloat(l.quantity) })));
                loadPending(kamFilter);
            } else {
                setMsg({ type:'error', text: res.status === 403 ? 'You can only approve SOs of your own parties.' : 'Approval failed' });
            }
        } catch(e) { setMsg({ type:'error', text:'Network error' }); }
        finally { setSaving(false); }
    }

    function confirmValidateAll() {
        if (!selectedSO) return;
        setConfirmModal({
            title: 'Approve all SKUs?',
            message: `This marks every SKU on SO ${selectedSO.voucher_no} as approved. This can't be edited afterwards.`,
            confirmLabel: 'Approve all SKUs',
            onConfirm: doValidateAll,
        });
    }

    async function addVendor() {
        if (!newVendor.vendor_name || !newVendor.kam_name) return;
        setVendorMsg(null);
        try {
            const res  = await fetch(`${API_BASE}/so-kam-review?action=add_vendor`, {
                method: 'POST',
                headers: { 'Content-Type':'application/json', ...adminHeaders() },
                body: JSON.stringify(newVendor)
            });
            if (!res.ok) {
                const text = await res.text();
                setVendorMsg({ type:'error', text: res.status === 403
                    ? 'Admin session expired — sign out and sign back in, then try again.'
                    : `Failed to save mapping: ${text || res.status}` });
                return;
            }
            const data = await res.json();
            if (data.success) {
                setNewVendor({ vendor_name:'', kam_name:'' });
                loadVendors(); loadKamList();
            }
        } catch(e) { console.error(e); setVendorMsg({ type:'error', text:'Network error — please try again.' }); }
    }

    async function deleteVendor(id) {
        setVendorMsg(null);
        try {
            const res = await fetch(`${API_BASE}/so-kam-review?action=delete_vendor`, {
                method: 'POST',
                headers: { 'Content-Type':'application/json', ...adminHeaders() },
                body: JSON.stringify({ id })
            });
            if (!res.ok) {
                const text = await res.text();
                setVendorMsg({ type:'error', text: res.status === 403
                    ? 'Admin session expired — sign out and sign back in, then try again.'
                    : `Failed to remove mapping: ${text || res.status}` });
                return;
            }
            loadVendors();
        } catch(e) { console.error(e); setVendorMsg({ type:'error', text:'Network error — please try again.' }); }
    }

    async function addPartyName() {
        if (!newPartyName.raw_name || !newPartyName.standard_name) return;
        setPartyNameMsg(null);
        try {
            const res  = await fetch(`${API_BASE}/so-kam-review?action=add_party_name`, {
                method: 'POST',
                headers: { 'Content-Type':'application/json', ...adminHeaders() },
                body: JSON.stringify(newPartyName)
            });
            if (!res.ok) {
                const text = await res.text();
                setPartyNameMsg({ type:'error', text: res.status === 403
                    ? 'Admin session expired — sign out and sign back in, then try again.'
                    : `Failed to save mapping: ${text || res.status}` });
                return;
            }
            const data = await res.json();
            if (data.success) {
                setNewPartyName({ raw_name:'', standard_name:'' });
                loadPartyNames();
                loadUnmappedNames();
            }
        } catch(e) { console.error(e); setPartyNameMsg({ type:'error', text:'Network error — please try again.' }); }
    }

    async function assignUnmapped(raw_name) {
        const standard_name = (unmappedAssign[raw_name] || '').trim();
        if (!standard_name) return;
        setUnmappedSaving(prev => ({ ...prev, [raw_name]: true }));
        setPartyNameMsg(null);
        try {
            const res  = await fetch(`${API_BASE}/so-kam-review?action=add_party_name`, {
                method: 'POST',
                headers: { 'Content-Type':'application/json', ...adminHeaders() },
                body: JSON.stringify({ raw_name, standard_name })
            });
            if (!res.ok) {
                const text = await res.text();
                setPartyNameMsg({ type:'error', text: res.status === 403
                    ? 'Admin session expired — sign out and sign back in, then try again.'
                    : `Failed to save mapping: ${text || res.status}` });
                return;
            }
            const data = await res.json();
            if (data.success) {
                setUnmappedAssign(prev => { const n = {...prev}; delete n[raw_name]; return n; });
                loadPartyNames();
                loadUnmappedNames();
            }
        } catch(e) { console.error(e); setPartyNameMsg({ type:'error', text:'Network error — please try again.' }); }
        finally { setUnmappedSaving(prev => ({ ...prev, [raw_name]: false })); }
    }

    async function deletePartyName(id) {
        setPartyNameMsg(null);
        try {
            const res = await fetch(`${API_BASE}/so-kam-review?action=delete_party_name`, {
                method: 'POST',
                headers: { 'Content-Type':'application/json', ...adminHeaders() },
                body: JSON.stringify({ id })
            });
            if (!res.ok) {
                const text = await res.text();
                setPartyNameMsg({ type:'error', text: res.status === 403
                    ? 'Admin session expired — sign out and sign back in, then try again.'
                    : `Failed to remove mapping: ${text || res.status}` });
                return;
            }
            loadPartyNames();
        } catch(e) { console.error(e); setPartyNameMsg({ type:'error', text:'Network error — please try again.' }); }
    }

    // ── Bulk "Approve multiple" ─────────────────────────────────────────
    async function enterBulkMode() {
        setBulkMode(true);
        setSelectedVouchers([]);
        const map = {};
        await Promise.all(pendingSOs.map(async so => {
            try {
                const res  = await fetch(`${API_BASE}/so-kam-review?action=detail&voucher_no=${encodeURIComponent(so.voucher_no)}${identityQS}`);
                const data = await res.json();
                map[so.voucher_no] = data.lines || [];
            } catch(e) { console.error(e); map[so.voucher_no] = []; }
        }));
        setBulkLinesMap(map);
    }

    function exitBulkMode() {
        setBulkMode(false);
        setSelectedVouchers([]);
        setBulkLinesMap({});
    }

    function toggleVoucherSelected(voucher_no) {
        setSelectedVouchers(prev => prev.includes(voucher_no)
            ? prev.filter(v => v !== voucher_no)
            : [...prev, voucher_no]);
    }

    function confirmBulkApprove() {
        if (selectedVouchers.length === 0) return;
        setConfirmModal({
            title: `Approve ${selectedVouchers.length} Sales Order${selectedVouchers.length !== 1 ? 's' : ''}?`,
            message: `This marks all selected SOs as approved. This can't be edited afterwards.`,
            confirmLabel: 'Approve Selected',
            onConfirm: doBulkApprove,
        });
    }

    async function doBulkApprove() {
        setBulkSaving(true);
        try {
            const res  = await fetch(`${API_BASE}/so-kam-review?action=validate_multiple`, {
                method: 'PUT',
                headers: { 'Content-Type':'application/json' },
                body: JSON.stringify({
                    voucher_nos:  selectedVouchers,
                    validated_by: user?.display_name || user?.username || 'KAM',
                    role: user?.role, username: user?.username,
                })
            });
            if (res.ok) {
                exitBulkMode();
                loadPending(kamFilter);
            } else {
                setMsg({ type:'error', text: res.status === 403 ? 'You can only approve SOs of your own parties.' : 'Bulk approval failed' });
            }
        } catch(e) { console.error(e); }
        finally { setBulkSaving(false); }
    }

    // ── "Auto approve all" — approves every SO currently in the list
    // (already limited to the KAM's own parties for KAM users, or to the
    // selected KAM filter for admins), each line at its SO qty.
    function confirmApproveAll() {
        if (pendingSOs.length === 0) return;
        setConfirmModal({
            title: `Auto approve all ${pendingSOs.length} SO${pendingSOs.length !== 1 ? 's' : ''}?`,
            message: `Every SO in this list${isAdmin && kamFilter ? ` (${kamFilter})` : ''} is approved at its SO quantity and released for warehouse scanning. This can't be edited afterwards.`,
            confirmLabel: 'Approve all',
            onConfirm: doApproveAll,
        });
    }

    async function doApproveAll() {
        setBulkSaving(true); setMsg(null);
        try {
            const res = await fetch(`${API_BASE}/so-kam-review?action=validate_multiple`, {
                method: 'PUT',
                headers: { 'Content-Type':'application/json' },
                body: JSON.stringify({
                    voucher_nos:  pendingSOs.map(so => so.voucher_no),
                    validated_by: user?.display_name || user?.username || 'KAM',
                    role: user?.role, username: user?.username,
                })
            });
            if (res.ok) {
                setMsg({ type:'success', text:`${pendingSOs.length} SO${pendingSOs.length !== 1 ? 's' : ''} approved` });
                loadPending(kamFilter);
            } else {
                setMsg({ type:'error', text: res.status === 403 ? 'You can only approve SOs of your own parties.' : 'Approval failed' });
            }
        } catch(e) { setMsg({ type:'error', text:'Network error' }); }
        finally { setBulkSaving(false); }
    }

    // ── VENDOR MANAGEMENT VIEW (admin only) ────────────────────────────────
    if (view === 'vendors' && isAdmin) return (
        <div>
            <ConfirmModalView modal={confirmModal} onClose={() => setConfirmModal(null)} />
            <div style={{ marginBottom:'24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                    <h1 style={{ fontSize:'28px', fontWeight:800, color:'#000', margin:0 }}>KAM — Vendor Mapping</h1>
                    <p style={{ fontSize:'14px', color:'#6B7280', marginTop:'4px', marginBottom:0 }}>Assign vendors to KAMs for SO routing</p>
                </div>
                <button onClick={() => setView('list')} style={secBtn}>← Back to SOs</button>
            </div>

            {/* Add new mapping */}
            <div style={{ background:'#fff', borderRadius:'12px', padding:'20px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', marginBottom:'20px' }}>
                <div style={{ fontSize:'14px', fontWeight:700, color:'#1A1A1A', marginBottom:'14px' }}>Add / Update Mapping</div>
                {vendorMsg && (
                    <div style={{ padding:'10px 14px', borderRadius:'8px', fontSize:'13px', fontWeight:600, marginBottom:'12px',
                        background: vendorMsg.type==='error' ? '#FEE2E2' : '#D1FAE5',
                        color:      vendorMsg.type==='error' ? '#991B1B' : '#065F46' }}>
                        {vendorMsg.text}
                    </div>
                )}
                <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
                    <input type="text" placeholder="Vendor name (exact or partial match)" value={newVendor.vendor_name}
                        onChange={e => setNewVendor(p => ({...p, vendor_name: e.target.value}))}
                        style={{ ...inpS, flex:2, minWidth:'220px' }} />
                    <select value={newVendor.kam_name} onChange={e => setNewVendor(p => ({...p, kam_name: e.target.value}))}
                        style={{ ...inpS, flex:1, minWidth:'160px' }}>
                        <option value="">Select KAM</option>
                        {KAMS.map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                    <button onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'so_kam_review')) return notifyEditBlocked(); addVendor(); }} style={priBtn}>Add / Update</button>
                </div>
            </div>

            {/* Vendor list */}
            <div style={{ background:'#fff', borderRadius:'12px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                        <thead>
                            <tr>
                                {['Sr. No.','Vendor Name','KAM','Last Updated',''].map(h => (
                                    <th key={h} style={thS}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {vendors.length === 0 ? (
                                <tr><td colSpan={5} style={{ padding:'40px', textAlign:'center', color:'#94A3B8' }}>No mappings yet</td></tr>
                            ) : vendors.map((v, i) => (
                                <tr key={v.id} style={{ background: i%2===0?'#fff':'#F8FAFC' }}>
                                    <td style={td}>{i+1}</td>
                                    <td style={{ ...td, fontWeight:500 }}>{v.vendor_name}</td>
                                    <td style={td}>
                                        <span style={{ padding:'3px 10px', borderRadius:'5px', background:'#EFF6FF', color:'#1D4ED8', fontSize:'12px', fontWeight:700 }}>{v.kam_name}</span>
                                    </td>
                                    <td style={{ ...td, color:'#94A3B8', fontSize:'12px' }}>{v.updated_at}</td>
                                    <td style={td}>
                                        <button onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'so_kam_review')) return notifyEditBlocked(); deleteVendor(v.id); }} style={{ padding:'4px 12px', background:'#FEE2E2', color:'#991B1B', border:'none', borderRadius:'6px', fontSize:'12px', cursor:'pointer', fontWeight:600 }}>Remove</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    // ── STANDARD PARTY NAMES VIEW (admin only) ──────────────────────────────
    if (view === 'party_names' && isAdmin) {
        const standardNameOptions = [...new Set(partyNames.map(p => p.standard_name))].sort();
        return (
        <div>
            <ConfirmModalView modal={confirmModal} onClose={() => setConfirmModal(null)} />
            <datalist id="standard-name-options">
                {standardNameOptions.map(n => <option key={n} value={n} />)}
            </datalist>
            <div style={{ marginBottom:'24px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                    <h1 style={{ fontSize:'28px', fontWeight:800, color:'#000', margin:0 }}>Standard Party Names</h1>
                    <p style={{ fontSize:'14px', color:'#6B7280', marginTop:'4px', marginBottom:0 }}>Map raw/alias party names to one standard name used across the portal</p>
                </div>
                <button onClick={() => setView('list')} style={secBtn}>← Back to SOs</button>
            </div>

            {partyNameMsg && (
                <div style={{ padding:'10px 14px', borderRadius:'8px', fontSize:'13px', fontWeight:600, marginBottom:'16px',
                    background: partyNameMsg.type==='error' ? '#FEE2E2' : '#D1FAE5',
                    color:      partyNameMsg.type==='error' ? '#991B1B' : '#065F46' }}>
                    {partyNameMsg.text}
                </div>
            )}

            {/* Add new mapping */}
            <div style={{ background:'#fff', borderRadius:'12px', padding:'20px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', marginBottom:'20px' }}>
                <div style={{ fontSize:'14px', fontWeight:700, color:'#1A1A1A', marginBottom:'14px' }}>Add / Update Mapping</div>
                <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
                    <input type="text" placeholder="Raw / alias party name (as it appears on the PO)" value={newPartyName.raw_name}
                        onChange={e => setNewPartyName(p => ({...p, raw_name: e.target.value}))}
                        style={{ ...inpS, flex:1, minWidth:'220px' }} />
                    <input type="text" list="standard-name-options" placeholder="Standard name — pick existing or type new" value={newPartyName.standard_name}
                        onChange={e => setNewPartyName(p => ({...p, standard_name: e.target.value}))}
                        style={{ ...inpS, flex:1, minWidth:'220px' }} />
                    <button onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'so_kam_review')) return notifyEditBlocked(); addPartyName(); }} style={priBtn}>Add / Update</button>
                </div>
            </div>

            {/* Unmapped party names — appear in PO/GRN data but have no mapping row yet */}
            <div style={{ background:'#fff', borderRadius:'12px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', overflow:'hidden', marginBottom:'20px' }}>
                <div style={{ padding:'16px 20px', borderBottom:'1px solid #F1F5F9' }}>
                    <div style={{ fontSize:'14px', fontWeight:700, color:'#1A1A1A' }}>Not Yet Standardized ({unmappedNames.length})</div>
                    <div style={{ fontSize:'12px', color:'#6B7280', marginTop:'2px' }}>Party names found in PO/GRN data with no mapping — assign a standard name so future ingestion standardizes automatically</div>
                </div>
                <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                        <thead>
                            <tr>
                                {['Raw Name','Rows','Assign Standard Name',''].map(h => (
                                    <th key={h} style={thS}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {unmappedNames.length === 0 ? (
                                <tr><td colSpan={4} style={{ padding:'40px', textAlign:'center', color:'#94A3B8' }}>Everything in PO/GRN data is standardized 🎉</td></tr>
                            ) : unmappedNames.map((u, i) => (
                                <tr key={u.raw_name} style={{ background: i%2===0?'#fff':'#F8FAFC' }}>
                                    <td style={{ ...td, fontWeight:500 }}>{u.raw_name}</td>
                                    <td style={{ ...td, color:'#94A3B8' }}>{u.row_count}</td>
                                    <td style={td}>
                                        <input type="text" list="standard-name-options" placeholder="Pick existing or type new"
                                            value={unmappedAssign[u.raw_name] || ''}
                                            onChange={e => setUnmappedAssign(prev => ({ ...prev, [u.raw_name]: e.target.value }))}
                                            style={{ ...inpS, minWidth:'220px' }} />
                                    </td>
                                    <td style={td}>
                                        <button
                                            disabled={!unmappedAssign[u.raw_name]?.trim() || unmappedSaving[u.raw_name]}
                                            onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'so_kam_review')) return notifyEditBlocked(); assignUnmapped(u.raw_name); }}
                                            style={{ ...priBtn, opacity: !unmappedAssign[u.raw_name]?.trim() ? 0.5 : 1 }}>
                                            {unmappedSaving[u.raw_name] ? 'Saving...' : 'Standardize'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Party name list */}
            <div style={{ background:'#fff', borderRadius:'12px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                        <thead>
                            <tr>
                                {['Sr. No.','Raw / Alias Name','Standard Name','Last Updated',''].map(h => (
                                    <th key={h} style={thS}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {partyNames.length === 0 ? (
                                <tr><td colSpan={5} style={{ padding:'40px', textAlign:'center', color:'#94A3B8' }}>No mappings yet</td></tr>
                            ) : partyNames.map((p, i) => (
                                <tr key={p.id} style={{ background: i%2===0?'#fff':'#F8FAFC' }}>
                                    <td style={td}>{i+1}</td>
                                    <td style={{ ...td, fontWeight:500 }}>{p.raw_name}</td>
                                    <td style={td}>
                                        <span style={{ padding:'3px 10px', borderRadius:'5px', background:'#EFF6FF', color:'#1D4ED8', fontSize:'12px', fontWeight:700 }}>{p.standard_name}</span>
                                    </td>
                                    <td style={{ ...td, color:'#94A3B8', fontSize:'12px' }}>{p.updated_at}</td>
                                    <td style={td}>
                                        <button onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'so_kam_review')) return notifyEditBlocked(); deletePartyName(p.id); }} style={{ padding:'4px 12px', background:'#FEE2E2', color:'#991B1B', border:'none', borderRadius:'6px', fontSize:'12px', cursor:'pointer', fontWeight:600 }}>Remove</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        );
    }

    // ── DETAIL VIEW ───────────────────────────────────────────────────────
    if (view === 'detail') return (
        <div className="kamr">
            <style>{KAM_CSS}</style>
            <ConfirmModalView modal={confirmModal} onClose={() => setConfirmModal(null)} />
            <KamHero compact subtitle={selectedSO ? `SO ${selectedSO.voucher_no} · ${selectedSO.buyer || selectedSO.consignee || ''}` : 'Loading…'} />
            <div style={{ marginBottom:'20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'12px' }}>
                <button onClick={() => { setView('list'); setSelectedSO(null); setMsg(null); }} style={secBtn}>← Back to List</button>
                {msg && (
                    <div style={{ padding:'8px 14px', borderRadius:'8px', fontSize:'13px', fontWeight:600,
                        background:msg.type==='success'?'#D1FAE5':'#FEE2E2',
                        color:msg.type==='success'?'#065F46':'#991B1B' }}>
                        {msg.text}
                    </div>
                )}
                {selectedSO?.kam_validated === 1 && (
                    <div style={{ padding:'8px 16px', borderRadius:'8px', background:'#D1FAE5', color:'#065F46', fontSize:'13px', fontWeight:700 }}>
                        ✓ Approved by {selectedSO.kam_validated_by}
                    </div>
                )}
            </div>

            {!selectedSO ? (
                <div style={{ padding:'60px', textAlign:'center', color:'#94A3B8' }}>Loading...</div>
            ) : (
                <>
                    {/* SO Header info */}
                    <div style={{ background:'#fff', borderRadius:'12px', padding:'20px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', marginBottom:'16px' }}>
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'16px' }}>
                            {[
                                ['Voucher No',  selectedSO.voucher_no],
                                ['SO Date',     formatDate(selectedSO.so_date)],
                                ['Buyer',       selectedSO.buyer],
                                ['Consignee',   selectedSO.consignee],
                                ['KAM',         selectedSO.kam_name || '—'],
                                ['Order Ref',   selectedSO.order_ref_no || '—'],
                                ['Total Qty',   selectedSO.total_quantity],
                                ['GSTIN',       selectedSO.gstin || '—'],
                            ].map(([label, value]) => (
                                <div key={label}>
                                    <div style={{ fontSize:'11px', fontWeight:700, color:'#94A3B8', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:'4px' }}>{label}</div>
                                    <div style={{ fontSize:'14px', fontWeight:600, color:'#1A1A1A' }}>{value}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Line items */}
                    <div style={{ background:'#fff', borderRadius:'12px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                        <div style={{ padding:'16px 20px', borderBottom:'1px solid #F1F5F9', fontSize:'14px', fontWeight:700, color:'#1A1A1A', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                            Line Items — {lines.length} SKUs
                            <StockSummary lines={lines} />
                        </div>
                        <div style={{ overflowX:'auto' }}>
                            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                                <thead>
                                    <tr>
                                        {['Sr. No.','EAN','SKU Name','Current Stock','Stock Status','MRP','SO Qty','Validated Qty','Variance'].map(h => (
                                            <th key={h} style={thS}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((l, i) => {
                                        const isEditing = !selectedSO.kam_validated;
                                        const currentQty = isEditing ? lineQtys[l.id] : l.kam_validated_qty;
                                        const variance = isEditing
                                            ? (currentQty === '' || currentQty == null ? null : parseFloat(currentQty) - parseFloat(l.quantity))
                                            : l.kam_variance;
                                        return (
                                            <tr key={l.id} style={{ background: i%2===0?'#fff':'#F8FAFC' }}>
                                                <td style={{ ...td, color:'#94A3B8' }}>{i+1}</td>
                                                <td style={{ ...td, fontFamily:'monospace', fontSize:'12px', color:'#6D28D9' }}>{l.ean || '—'}</td>
                                                <td style={{ ...td, maxWidth:'260px', whiteSpace:'normal', fontWeight:500 }}>{l.sku_name}</td>
                                                <td style={td}>{l.current_stock != null ? Number(l.current_stock).toLocaleString('en-IN') : '—'}</td>
                                                <td style={td}><StockBadge stock={l.current_stock} qty={l.quantity} /></td>
                                                <td style={td}>₹{l.mrp || '—'}</td>
                                                <td style={{ ...td, fontWeight:700, color:'#1D4ED8' }}>{Number(l.quantity).toLocaleString('en-IN')}</td>
                                                <td style={td}>
                                                    {isEditing ? (
                                                        <input
                                                            type="number"
                                                            value={lineQtys[l.id] ?? ''}
                                                            onChange={e => setLineQtys(prev => ({ ...prev, [l.id]: e.target.value }))}
                                                            style={{ width:'80px', padding:'5px 8px', border:'1.5px solid #C4B5FD', borderRadius:'6px', fontSize:'13px', outline:'none' }}
                                                        />
                                                    ) : (
                                                        <span style={{ fontWeight:700, color:'#065F46' }}>
                                                            {l.kam_validated_qty != null ? Number(l.kam_validated_qty).toLocaleString('en-IN') : '—'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={td}>
                                                    {variance != null && !isNaN(variance) ? (
                                                        <span style={{ fontWeight:700, fontSize:'12px', padding:'2px 8px', borderRadius:'5px',
                                                            background: variance === 0 ? '#D1FAE5' : variance > 0 ? '#DBEAFE' : '#FEE2E2',
                                                            color:      variance === 0 ? '#065F46' : variance > 0 ? '#1D4ED8' : '#991B1B' }}>
                                                            {variance > 0 ? `+${variance}` : variance}
                                                        </span>
                                                    ) : <span style={{ color:'#CBD5E1' }}>—</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {selectedSO && !selectedSO.kam_validated && (
                        <button onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'so_kam_review')) return notifyEditBlocked(); confirmValidateAll(); }} disabled={saving} style={bigApproveBtn}>
                            {saving ? 'Saving...' : 'Approve'}
                        </button>
                    )}
                </>
            )}
        </div>
    );

    // ── LIST VIEW (default) ───────────────────────────────────────────────
    const showAutoApproveToggle = isAdmin ? !!kamFilter : true;

    return (
        <div data-tutorial="so-kam-table" className="kamr">
            <style>{KAM_CSS}</style>
            <ConfirmModalView modal={confirmModal} onClose={() => setConfirmModal(null)} />
            <KamHero
                subtitle={isAdmin ? 'All pending Sales Orders' : `Your pending Sales Orders${user?.display_name ? ` — ${user.display_name}` : ''}`}
                stats={!loading ? [
                    { label: 'Pending SOs', value: pendingSOs.length, tone: 'violet' },
                    { label: 'No KAM assigned', value: pendingSOs.filter(so => !so.kam_name).length, tone: 'amber' },
                    { label: 'Out-of-stock lines', value: pendingSOs.reduce((a, so) => a + (so.out_lines || 0), 0), tone: 'red' },
                    { label: 'Low-stock lines', value: pendingSOs.reduce((a, so) => a + (so.low_lines || 0), 0), tone: 'gold' },
                ] : null}
                actions={isAdmin && (
                    <>
                        <button className="kamr-hero-btn" onClick={() => { setView('vendors'); loadVendors(); }}>⚙ KAM — Vendor Mapping</button>
                        <button className="kamr-hero-btn" onClick={() => { setView('party_names'); loadPartyNames(); loadUnmappedNames(); }}>⚙ Standard Party Names</button>
                    </>
                )}
            />
            {msg && (
                <div style={{ padding:'10px 16px', borderRadius:'10px', fontSize:'13px', fontWeight:600, marginBottom:16,
                    background:msg.type==='success'?'#D1FAE5':'#FEE2E2', color:msg.type==='success'?'#065F46':'#991B1B' }}>
                    {msg.text}
                </div>
            )}

            {/* KAM filter — admin only */}
            {isAdmin && (
                <div style={{ display:'flex', gap:'8px', marginBottom:'20px', flexWrap:'wrap' }}>
                    <button onClick={() => { setKamFilter(''); loadPending(''); }}
                        style={{ ...filterBtn, background: kamFilter===''?'#0F1F2E':'#F1F5F9', color: kamFilter===''?'#fff':'#64748B' }}>
                        All KAMs
                    </button>
                    {kamList.map(k => (
                        <button key={k} onClick={() => { setKamFilter(k); loadPending(k); }}
                            style={{ ...filterBtn, background: kamFilter===k?'#0F1F2E':'#F1F5F9', color: kamFilter===k?'#fff':'#64748B' }}>
                            {k}
                        </button>
                    ))}
                </div>
            )}

            {/* Bulk / Auto-approve toolbar */}
            <div style={{ display:'flex', gap:'20px', alignItems:'center', flexWrap:'wrap', marginBottom:'20px', padding:'14px 18px', background:'#fff', borderRadius:'12px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                <ToggleRow
                    label="Approve multiple"
                    checked={bulkMode}
                    onChange={() => bulkMode ? exitBulkMode() : enterBulkMode()}
                />
                <button
                    className="kamr-approve-all"
                    disabled={bulkSaving || loading || pendingSOs.length === 0}
                    onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'so_kam_review')) return notifyEditBlocked(); confirmApproveAll(); }}
                    title="Approve every SO in this list at its SO quantity"
                >
                    ⚡ Auto approve all{pendingSOs.length ? ` (${pendingSOs.length})` : ''}
                </button>
                {showAutoApproveToggle && (
                    <ToggleRow
                        label={isAdmin ? `Auto-approve new SOs — ${kamFilter} (no KAM alerts)` : 'Auto-approve new SOs (no KAM alerts)'}
                        checked={autoApprove.enabled}
                        disabled={autoApprove.loading}
                        onChange={toggleAutoApprove}
                    />
                )}
                {bulkMode && selectedVouchers.length > 0 && (
                    <button onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'so_kam_review')) return notifyEditBlocked(); confirmBulkApprove(); }} disabled={bulkSaving} style={priBtn}>
                        {bulkSaving ? 'Saving...' : `Approve Selected (${selectedVouchers.length})`}
                    </button>
                )}
            </div>

            {loading ? (
                <div style={{ padding:'60px', textAlign:'center', color:'#94A3B8' }}>Loading...</div>
            ) : pendingSOs.length === 0 ? (
                <div style={{ background:'#fff', borderRadius:'14px', padding:'60px', textAlign:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize:'40px', marginBottom:'16px' }}>✅</div>
                    <div style={{ fontSize:'18px', fontWeight:700, color:'#1A1A1A', marginBottom:'8px' }}>No pending SOs</div>
                    <div style={{ fontSize:'14px', color:'#6B7280' }}>All Sales Orders have been validated — the warehouse can scan them. 🎉</div>
                </div>
            ) : bulkMode ? (
                <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
                    {pendingSOs.map(so => (
                        <div key={so.id} style={{ background:'#fff', borderRadius:'12px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                            <div style={{ padding:'14px 18px', display:'flex', alignItems:'center', gap:'12px', borderBottom:'1px solid #F1F5F9' }}>
                                <input type="checkbox"
                                    checked={selectedVouchers.includes(so.voucher_no)}
                                    onChange={() => toggleVoucherSelected(so.voucher_no)}
                                    style={{ width:'18px', height:'18px', cursor:'pointer' }} />
                                <div style={{ fontWeight:700, color:'#1D4ED8', fontFamily:'monospace' }}>{so.voucher_no}</div>
                                <div style={{ fontWeight:500 }}>{so.buyer}</div>
                                {so.kam_name && (
                                    <span style={{ padding:'3px 10px', borderRadius:'5px', background:'#EFF6FF', color:'#1D4ED8', fontSize:'12px', fontWeight:700 }}>{so.kam_name}</span>
                                )}
                            </div>
                            <div style={{ overflowX:'auto' }}>
                                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                                    <thead>
                                        <tr>
                                            {['SKU Name','Current Stock','Stock Status','SO Qty','MRP'].map(h => <th key={h} style={thS}>{h}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(bulkLinesMap[so.voucher_no] || []).map((l, i) => (
                                            <tr key={l.id} style={{ background: i%2===0?'#fff':'#F8FAFC' }}>
                                                <td style={{ ...td, maxWidth:'320px', whiteSpace:'normal', fontWeight:500 }}>{l.sku_name}</td>
                                                <td style={td}>{l.current_stock != null ? Number(l.current_stock).toLocaleString('en-IN') : '—'}</td>
                                                <td style={td}><StockBadge stock={l.current_stock} qty={l.quantity} /></td>
                                                <td style={{ ...td, fontWeight:700, color:'#1D4ED8' }}>{Number(l.quantity).toLocaleString('en-IN')}</td>
                                                <td style={td}>₹{l.mrp || '—'}</td>
                                            </tr>
                                        ))}
                                        {(!bulkLinesMap[so.voucher_no]) && (
                                            <tr><td colSpan={5} style={{ ...td, textAlign:'center', color:'#94A3B8' }}>Loading SKUs...</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ background:'#fff', borderRadius:'12px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                    <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                            <thead>
                                <tr>
                                    {['Sr. No.','Voucher No','Date','Buyer / Consignee','KAM','SKUs','Stock','Total Qty','Action'].map(h => (
                                        <th key={h} style={thS}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pendingSOs.map((so, i) => (
                                    <tr key={so.id} className="kamr-row" style={{ background: !so.kam_name ? '#FFF8EB' : i%2===0?'#fff':'#F8FAFC' }}>
                                        <td style={{ ...td, color:'#94A3B8' }}>{i+1}</td>
                                        <td style={{ ...td, fontWeight:700, color:'#1D4ED8', fontFamily:'monospace' }}>{so.voucher_no}</td>
                                        <td style={td}>{formatDate(so.so_date)}</td>
                                        <td style={{ ...td, maxWidth:'200px', whiteSpace:'normal' }}>
                                            <div style={{ fontWeight:500 }}>{so.buyer}</div>
                                            {so.consignee && so.consignee !== so.buyer && (
                                                <div style={{ fontSize:'11px', color:'#64748B', marginTop:'2px' }}>→ {so.consignee}</div>
                                            )}
                                        </td>
                                        <td style={td}>
                                            {so.kam_name
                                                ? <span style={{ padding:'3px 10px', borderRadius:'5px', background:'#EFF6FF', color:'#1D4ED8', fontSize:'12px', fontWeight:700 }}>{so.kam_name}</span>
                                                : <span style={{ padding:'3px 10px', borderRadius:'5px', background:'#FDF0D5', color:'#9A6400', fontSize:'12px', fontWeight:700 }}>⚠ Unassigned</span>}
                                        </td>
                                        <td style={{ ...td, fontWeight:600 }}>{so.line_count}</td>
                                        <td style={td}>
                                            {so.out_lines > 0 && <span className="kamr-chip" style={{ color:'#B4232C', background:'#FDE2E1' }}>{so.out_lines} out</span>}
                                            {so.low_lines > 0 && <span className="kamr-chip" style={{ color:'#9A6400', background:'#FDF0D5' }}>{so.low_lines} low</span>}
                                            {!so.out_lines && !so.low_lines && <span className="kamr-chip" style={{ color:'#2F7A4B', background:'#DFF2E6' }}>✓ Healthy</span>}
                                        </td>
                                        <td style={{ ...td, fontWeight:700 }}>{Number(so.total_quantity||0).toLocaleString('en-IN')}</td>
                                        <td style={td}>
                                            <button onClick={() => openSO(so)} className="kamr-open" style={priBtn}>
                                                Review &amp; Approve →
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

// Stock status vs this line's SO qty (same rule as the alert email in
// backend/shared/kamReviewAlert.js): 0 = Out of Stock, below SO qty = Low,
// covers SO qty = Healthy; no row in the latest inventory file = Unknown.
function stockStatusOf(stock, qty) {
    if (stock === null || stock === undefined) return { key: 'unknown', label: 'Unknown', color: '#7A7090', bg: '#F1EEF6' };
    if (Number(stock) <= 0) return { key: 'out', label: 'Out of Stock', color: '#B4232C', bg: '#FDE2E1' };
    if (Number(stock) < Number(qty || 0)) return { key: 'low', label: 'Low', color: '#9A6400', bg: '#FDF0D5' };
    return { key: 'healthy', label: 'Healthy', color: '#2F7A4B', bg: '#DFF2E6' };
}

function StockBadge({ stock, qty }) {
    const st = stockStatusOf(stock, qty);
    return <span className="kamr-chip" style={{ color: st.color, background: st.bg }}><span className="kamr-dot" style={{ background: st.color }} />{st.label}</span>;
}

function StockSummary({ lines }) {
    const c = lines.reduce((a, l) => { a[stockStatusOf(l.current_stock, l.quantity).key]++; return a; }, { out: 0, low: 0, healthy: 0, unknown: 0 });
    return (
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
            {c.out > 0 && <span className="kamr-chip" style={{ color: '#B4232C', background: '#FDE2E1' }}>{c.out} out of stock</span>}
            {c.low > 0 && <span className="kamr-chip" style={{ color: '#9A6400', background: '#FDF0D5' }}>{c.low} low</span>}
            {c.healthy > 0 && <span className="kamr-chip" style={{ color: '#2F7A4B', background: '#DFF2E6' }}>{c.healthy} healthy</span>}
            {c.unknown > 0 && <span className="kamr-chip" style={{ color: '#7A7090', background: '#F1EEF6' }}>{c.unknown} unknown</span>}
        </span>
    );
}

// Dark banner (Gifting & Dispatch style) with greyed-out warehouse photos.
function KamHero({ subtitle, stats, actions, compact }) {
    return (
        <div className={`kamr-hero${compact ? ' is-compact' : ''}`}>
            <div className="kamr-hero-bg" aria-hidden="true">
                <img src={imgConveyor} alt="" className="kamr-img kamr-img-1" />
                <img src={imgScanBox}  alt="" className="kamr-img kamr-img-2" />
                <img src={imgCounter}  alt="" className="kamr-img kamr-img-3" />
                <img src={imgHandheld} alt="" className="kamr-img kamr-img-4" />
                <div className="kamr-fade" />
                <span className="kamr-float kamr-float-1">✅</span>
                <span className="kamr-float kamr-float-2">📦</span>
                <span className="kamr-float kamr-float-3">✔️</span>
            </div>
            <div className="kamr-hero-content">
                <div>
                    <div className="kamr-kicker">PO &amp; SO Lifecycle · SO KAM Review</div>
                    <h1 className="kamr-title">Sales Order Validation <b>By KAM</b></h1>
                    <p className="kamr-hint">Kindly validate SO based on current stock and requirements.</p>
                    {subtitle && <p className="kamr-sub">{subtitle}</p>}
                </div>
                {actions && <div className="kamr-hero-actions">{actions}</div>}
            </div>
            {stats && (
                <div className="kamr-stats">
                    {stats.map(st => (
                        <div key={st.label} className={`kamr-stat tone-${st.tone}`}>
                            <span className="kamr-stat-n">{st.value.toLocaleString('en-IN')}</span>
                            <span className="kamr-stat-l">{st.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const KAM_CSS = `
.kamr-hero { position: relative; overflow: hidden; border-radius: 20px; padding: 28px 30px; margin-bottom: 20px;
  background: linear-gradient(135deg, #2A2140 0%, #1A1529 55%, #120D1D 100%); border: 1px solid #2A2338; box-shadow: 0 10px 30px rgba(20,16,31,0.3); }
.kamr-hero.is-compact { padding: 22px 26px; }
.kamr-hero-bg { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.kamr-img { position: absolute; object-fit: cover; border-radius: 16px; filter: grayscale(1) contrast(1.1); opacity: 0.17; animation: kamr-drift 12s ease-in-out infinite; }
.kamr-img-1 { width: 240px; height: 170px; right: 3%;  top: -20px; transform: rotate(4deg); }
.kamr-img-2 { width: 200px; height: 160px; right: 21%; top: 46px;  transform: rotate(-4deg); animation-delay: 1.2s; animation-duration: 13s; }
.kamr-img-3 { width: 230px; height: 135px; right: 38%; top: -14px; transform: rotate(3deg); animation-delay: .6s; animation-duration: 11s; }
.kamr-img-4 { width: 210px; height: 125px; right: 55%; top: 70px;  transform: rotate(-3deg); animation-delay: 2s; animation-duration: 14s; }
.kamr-fade { position: absolute; inset: 0; background: linear-gradient(90deg, #1E1830 28%, rgba(30,24,48,0.55) 60%, rgba(30,24,48,0.12) 100%); }
.kamr-float { position: absolute; font-size: 24px; opacity: .18; animation: kamr-float 9s ease-in-out infinite; }
.kamr-float-1 { left: 3%; top: 72%; } .kamr-float-2 { left: 48%; top: 10%; animation-delay: 1s; } .kamr-float-3 { left: 92%; top: 74%; animation-delay: 2s; }
@keyframes kamr-drift { 0%,100% { translate: 0 0; } 50% { translate: 0 -10px; } }
@keyframes kamr-float { 0%,100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-12px) rotate(8deg); } }
.kamr-hero-content { position: relative; z-index: 1; display: flex; justify-content: space-between; align-items: flex-start; gap: 18px; flex-wrap: wrap; }
.kamr-kicker { font-size: 12px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: #A48FD1; }
.kamr-title { font-size: 30px; font-weight: 500; margin: 6px 0 6px; color: #fff; letter-spacing: -0.01em; }
.kamr-hero.is-compact .kamr-title { font-size: 24px; }
.kamr-hint { font-size: 14px; color: #9A90AE; font-style: italic; margin: 0; }
.kamr-sub { font-size: 13px; color: #C9BFE0; margin: 8px 0 0; }
.kamr-hero-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.kamr-hero-btn { padding: 10px 16px; border-radius: 10px; border: 1px solid rgba(255,255,255,0.22); background: rgba(255,255,255,0.08); color: #fff; font-weight: 700; font-size: 13px; cursor: pointer; transition: transform .15s ease, background .15s ease; }
.kamr-hero-btn:hover { transform: translateY(-2px); background: rgba(255,255,255,0.16); }
.kamr-stats { position: relative; z-index: 1; display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin-top: 20px; padding-top: 18px; border-top: 1px solid rgba(255,255,255,0.12); }
.kamr-stat { display: flex; flex-direction: column; gap: 2px; padding: 12px 14px; border-radius: 12px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); transition: transform .15s ease; }
.kamr-stat:hover { transform: translateY(-2px); }
.kamr-stat-n { font-size: 24px; font-weight: 800; line-height: 1.1; }
.kamr-stat-l { font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #B2AAC4; }
.tone-violet .kamr-stat-n { color: #C9B8FF; } .tone-amber .kamr-stat-n { color: #FFC56B; } .tone-red .kamr-stat-n { color: #FF8A8F; } .tone-gold .kamr-stat-n { color: #F6D57A; }
.kamr-chip { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 999px; font-size: 11.5px; font-weight: 700; margin-right: 4px; white-space: nowrap; }
.kamr-dot { width: 7px; height: 7px; border-radius: 50%; }
.kamr-approve-all { padding: 10px 18px; border: none; border-radius: 10px; font-weight: 800; font-size: 13px; cursor: pointer; color: #fff;
  background: linear-gradient(135deg, #16A34A, #0E7A37); box-shadow: 0 6px 14px rgba(22,163,74,0.3); transition: transform .15s ease, box-shadow .15s ease; }
.kamr-approve-all:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(22,163,74,0.4); }
.kamr-approve-all:disabled { opacity: .45; cursor: default; box-shadow: none; }
.kamr-row { transition: background .12s ease; }
.kamr-row:hover { background: #F3EEFF !important; }
.kamr-open { transition: transform .15s ease; }
.kamr-open:hover { transform: translateX(3px); }
@media (prefers-reduced-motion: reduce) { .kamr-img, .kamr-float { animation: none !important; } }
@media (max-width: 760px) { .kamr-img { display: none; } .kamr-title { font-size: 24px; } }
`;

function ToggleRow({ label, checked, onChange, disabled }) {
    return (
        <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
            <span style={{
                position:'relative', width:'40px', height:'22px', borderRadius:'999px',
                background: checked ? '#0F1F2E' : '#E2E8F0', transition:'background 0.15s',
            }}>
                <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange}
                    style={{ position:'absolute', opacity:0, width:'100%', height:'100%', margin:0, cursor: disabled ? 'default' : 'pointer' }} />
                <span style={{
                    position:'absolute', top:'2px', left: checked ? '20px' : '2px', width:'18px', height:'18px',
                    borderRadius:'50%', background:'#fff', transition:'left 0.15s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)',
                }} />
            </span>
            <span style={{ fontSize:'13px', fontWeight:600, color:'#334155' }}>{label}</span>
        </label>
    );
}

function ConfirmModalView({ modal, onClose }) {
    if (!modal) return null;
    return (
        <div style={{
            position:'fixed', inset:0, background:'rgba(15,31,46,0.45)', zIndex:1000,
            display:'flex', alignItems:'center', justifyContent:'center', padding:'20px',
        }} onClick={onClose}>
            <div onClick={e => e.stopPropagation()} style={{
                background:'#fff', borderRadius:'14px', padding:'26px', maxWidth:'420px', width:'100%',
                boxShadow:'0 20px 60px rgba(0,0,0,0.3)',
            }}>
                <div style={{ fontSize:'18px', fontWeight:800, color:'#0F1F2E', marginBottom:'10px' }}>{modal.title}</div>
                <div style={{ fontSize:'14px', color:'#475569', lineHeight:1.5, marginBottom:'22px' }}>{modal.message}</div>
                <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px' }}>
                    <button onClick={onClose} style={secBtn}>Cancel</button>
                    <button onClick={() => { modal.onConfirm(); onClose(); }} style={priBtn}>
                        {modal.confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

const td        = { padding:'11px 14px', borderBottom:'1px solid #F1F5F9', color:'#334155', verticalAlign:'middle', whiteSpace:'nowrap' };
const thS       = { background:'#0F1F2E', color:'#fff', padding:'11px 14px', textAlign:'left', fontSize:'12px', fontWeight:600, whiteSpace:'nowrap' };
const priBtn    = { padding:'8px 18px', background:'#0F1F2E', color:'#fff', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:700, cursor:'pointer' };
const secBtn    = { padding:'8px 16px', background:'#F1F5F9', color:'#475569', border:'1px solid #E2E8F0', borderRadius:'8px', fontSize:'13px', fontWeight:600, cursor:'pointer' };
const filterBtn = { padding:'7px 16px', borderRadius:'20px', border:'none', fontSize:'13px', fontWeight:600, cursor:'pointer', transition:'all 0.15s' };
const inpS      = { padding:'9px 12px', border:'1.5px solid #E2E8F0', borderRadius:'8px', fontSize:'13px', outline:'none', background:'#fff' };
const bigApproveBtn = {
    display:'block', width:'100%', marginTop:'20px', padding:'18px', background:'#0F1F2E', color:'#fff',
    border:'none', borderRadius:'12px', fontSize:'16px', fontWeight:700, cursor:'pointer', letterSpacing:'0.02em',
};
